import { SELECTED_LEG_ID_PLS_PARAM } from '@pages/FlightSearchResult/constants';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { useFlightContext } from '@context/FlightContext';

import { TripViewModel } from '@wegoTypes/flights/tripViewModel';

type DepartReturn = 'depart' | 'return';

type FlightPerLegSearchProviderValue = {
  isPLSChangeLegLoading: boolean;
  isPerLegSearch: boolean;
  isPLSDepartLegSelection: boolean;
  handlePLSLegSelect: (
    legType: DepartReturn,
    tripViewModel: TripViewModel,
    searchId?: string,
  ) => void;
  handlePLSLegClear: () => void;
  currentlySelectedLegIdPLS: string | null;
  previouslySelectedLegIdPLS: string;
};

export const FlightPerLegSearchContext = createContext<FlightPerLegSearchProviderValue>(
  null as unknown as FlightPerLegSearchProviderValue,
);

type FlightPerLegSearchProviderProps = {
  children: React.ReactNode;
};

const FlightPerLegSearchProvider = ({ children }: FlightPerLegSearchProviderProps) => {
  const { isFlightSearch2 } = useFlightContext();

  const [isPLSChangeLegLoading, setIsPLSChangeLegLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [previouslySelectedLegIdPLS, setPreviouslySelectedLegIdPLS] = useState('');
  const [departLegScrollY, setDepartLegScrollY] = useState(0);
  const currentlySelectedLegIdPLS = searchParams.get(SELECTED_LEG_ID_PLS_PARAM);
  const isFirstMount = useRef(true);
  const { isRoundTrip } = useFlightSearchPageParams();

  const isPerLegSearch = isFlightSearch2 && isRoundTrip;
  const isPLSDepartLegSelection = isPerLegSearch && !currentlySelectedLegIdPLS;

  const handlePLSLegSelect = useCallback(
    (legType: DepartReturn, tripViewModel: TripViewModel, searchId) => {
      if (legType === 'depart') {
        searchParams.set(SELECTED_LEG_ID_PLS_PARAM, tripViewModel.legs[0].leg.id);

        setSearchParams(searchParams);
        setDepartLegScrollY(window.scrollY);

        return;
      }

      searchParams.delete('selected_trip_id');
      searchParams.set('selected_trip_id', tripViewModel.tripId);
      searchParams.delete('search_id');
      searchParams.set('search_id', searchId);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const handlePLSLegClear = useCallback(() => {
    searchParams.delete(SELECTED_LEG_ID_PLS_PARAM);

    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  // Fake loading between leg select
  useEffect(() => {
    if (!isFirstMount.current) {
      setIsPLSChangeLegLoading(true);

      const timeout = setTimeout(
        () => {
          setIsPLSChangeLegLoading(false);
        },
        isPLSDepartLegSelection ? 750 : 1500,
      );

      return () => {
        clearTimeout(timeout);
      };
    }

    isFirstMount.current = false;
  }, [isPLSDepartLegSelection]);

  // Scroll to the previously selected depart leg when coming back to depart leg selection
  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (currentlySelectedLegIdPLS) {
      setPreviouslySelectedLegIdPLS(currentlySelectedLegIdPLS);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      timeout = setTimeout(() => {
        window.scrollTo({ top: departLegScrollY, behavior: 'smooth' });
      }, 750);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [currentlySelectedLegIdPLS, departLegScrollY]);

  const providerValue = useMemo<FlightPerLegSearchProviderValue>(
    () => ({
      currentlySelectedLegIdPLS,
      handlePLSLegClear,
      handlePLSLegSelect,
      isPLSChangeLegLoading,
      previouslySelectedLegIdPLS,
      isPerLegSearch,
      isPLSDepartLegSelection,
    }),
    [
      currentlySelectedLegIdPLS,
      handlePLSLegClear,
      handlePLSLegSelect,
      isPLSChangeLegLoading,
      previouslySelectedLegIdPLS,
      isPerLegSearch,
      isPLSDepartLegSelection,
    ],
  );

  return (
    <FlightPerLegSearchContext.Provider value={providerValue}>
      {children}
    </FlightPerLegSearchContext.Provider>
  );
};

export const useFlightPLS = () => useContext(FlightPerLegSearchContext);

export default FlightPerLegSearchProvider;
